import type { ReactNode } from 'react';
import { Box, Flex, Loader, Text, UnstyledButton } from '@mantine/core';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { MeetingPlatformIcon } from 'src/meetings/components/MeetingPlatformIcon';
import {
  MeetingNoteStatus,
  useCalendarEventMeetingNote,
} from 'src/meetings/hooks/useCalendarEventMeetingNote';
import { useTranscript } from 'src/meetings/hooks/useTranscript';
import { TranscriptionStatus } from 'src/meetings/detail/tabs/transcript/types';
import { formatEventTime, formatMeetingDuration, isPastEvent } from 'src/meetings/utils/date-utils';
import { getParticipantDisplayNames } from 'src/meetings/utils/event-utils';
import type { FamilyMeeting } from 'src/meetings/hooks/useScopedMeetings';
import { ParticipantPill } from './ParticipantPill';

type Props = {
  meeting: FamilyMeeting;
  onOpenParticipants?: (eventId: string) => void;
  onOpenDetails?: (eventId: string) => void;
  onGenerateMeetingNote?: (eventId: string) => void;
};

export function MeetingCard({
  meeting,
  onOpenParticipants,
  onOpenDetails,
  onGenerateMeetingNote,
}: Props) {
  const participantNames = getParticipantDisplayNames(meeting);
  const isPast = isPastEvent(meeting);

  // Only look up transcript status for past meetings that actually have one.
  const { transcript, isError: isTranscriptError } = useTranscript({
    eventId: meeting.id,
    enabled: isPast && !!meeting.hasTranscription,
  });

  const transcriptReady = transcript?.status === TranscriptionStatus.READY;
  const { meetingNote } = useCalendarEventMeetingNote(meeting.id, {
    enabled: isPast && !!meeting.hasTranscription && transcriptReady,
  });
  const noteExistsOrGenerating =
    meetingNote?.status === MeetingNoteStatus.READY ||
    meetingNote?.status === MeetingNoteStatus.MEETING_NOTE_PROCESSING;

  const footer = renderFooter();

  return (
    <Box
      onClick={() => onOpenDetails?.(meeting.id)}
      style={{
        borderRadius: 20,
        border: '1px solid var(--mantine-color-gray-1)',
        overflow: 'hidden',
        cursor: onOpenDetails ? 'pointer' : undefined,
      }}
    >
      <Box px="md" pt="md" pb="lg">
        <Box>
          <Text size="textMd" fw={FONT_WEIGHTS.medium} c="gray.10">
            {meeting.title}
          </Text>
          <Flex align="center" gap="xxxs" mt="xxxs">
            {meeting.meetingPlatform && (
              <MeetingPlatformIcon platform={meeting.meetingPlatform} size={16} />
            )}
            <Text size="textXs" fw={FONT_WEIGHTS.medium} c="gray.5">
              {formatTimeWithDuration(meeting)}
            </Text>
          </Flex>
        </Box>

        {participantNames.length > 0 && (
          <Box mt="sm" w="fit-content" onClick={event => event.stopPropagation()}>
            <ParticipantPill
              participantNames={participantNames}
              onOpen={onOpenParticipants ? () => onOpenParticipants(meeting.id) : undefined}
            />
          </Box>
        )}
      </Box>

      <Box
        px="md"
        style={{
          borderTop: '1px solid var(--mantine-color-gray-1)',
          paddingTop: 13,
          paddingBottom: 17,
        }}
      >
        {footer}
      </Box>
    </Box>
  );

  function renderFooter(): ReactNode {
    if (isPast && meeting.hasTranscription) {
      if (transcript?.status === TranscriptionStatus.READY) {
        // Once a note exists or is generating, drop the generate action.
        if (!noteExistsOrGenerating) {
          return (
            <UnstyledButton
              onClick={event => {
                event.stopPropagation();
                onGenerateMeetingNote?.(meeting.id);
              }}
            >
              <Text size="textXs" fw={FONT_WEIGHTS.semibold} c="brand.6">
                Generate meeting note
              </Text>
            </UnstyledButton>
          );
        }
        if (meetingNote?.status === MeetingNoteStatus.MEETING_NOTE_PROCESSING) {
          return (
            <Flex align="center" gap="xxs">
              <Loader size={20} color="gray.5" />
              <Text size="textXs" fw={FONT_WEIGHTS.medium} c="gray.6">
                Generating meeting note...
              </Text>
            </Flex>
          );
        }
        return (
          <Text size="textXs" fw={FONT_WEIGHTS.medium} c="gray.6">
            Meeting note generated
          </Text>
        );
      }

      // A transcript exists but isn't ready yet — show processing while it's
      // actually being processed (or still loading its status). On a fetch error
      // we fall through to the standard footer rather than spin forever.
      if (
        !isTranscriptError &&
        (transcript === undefined || transcript.status === TranscriptionStatus.PROCESSING)
      ) {
        return (
          <Flex align="center" gap="xxs">
            <Loader size={20} color="gray.5" />
            <Text size="textXs" fw={FONT_WEIGHTS.medium} c="gray.6">
              Processing transcript...
            </Text>
          </Flex>
        );
      }
    }

    // Upcoming meetings, past meetings with no transcript, or a failed/empty
    // transcript all fall back to the standard recording footer.
    return (
      <Text size="textXs" fw={FONT_WEIGHTS.medium} c="gray.6">
        Recording: Enabled
      </Text>
    );
  }
}

function formatTimeWithDuration(meeting: FamilyMeeting): string {
  const start = meeting.startAt ?? meeting.scheduledStartAt;
  const end = meeting.endAt ?? meeting.scheduledEndAt;
  const range = formatEventTime(start, end).replace(/ (AM|PM)/g, '$1');
  return `${range} (${formatMeetingDuration(start, end)})`;
}
